import React, { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Assignment,
  BarChart,
  CalendarMonth,
  Campaign,
  Dashboard,
  DirectionsBus,
  EventNote,
  ExpandMore,
  FactCheck,
  Groups,
  Hotel,
  LibraryBooks,
  Logout,
  Menu as MenuIcon,
  NotificationsNone,
  Payments,
  People,
  Person,
  School,
  Search,
  Security,
  Settings,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 256;

const shell = {
  ink: '#071637',
  navy: '#061b3f',
  navy2: '#082b62',
  active: '#1d64d8',
  page: '#f6f9fe',
  line: '#e2e8f2',
  muted: '#52617a',
  gold: '#f4b63f',
};

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const getMenuItems = () => {
    if (user?.role === 'teacher') {
      return [
        { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
        { text: 'Attendance', icon: <EventNote />, path: '/teacher/attendance' },
        { text: 'Marks', icon: <Assignment />, path: '/teacher/marks' },
        { text: 'My Classes', icon: <People />, path: '/teacher/classes' },
        { text: 'Reports', icon: <BarChart />, path: '/teacher/reports' },
      ];
    }

    if (user?.role === 'student') {
      return [
        { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
        { text: 'My Attendance', icon: <EventNote />, path: '/student/attendance' },
        { text: 'My Marks', icon: <Assignment />, path: '/student/marks' },
        { text: 'Exams', icon: <School />, path: '/student/exams' },
      ];
    }

    return [
      { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
      { text: 'Students', icon: <People />, path: '/admin/students' },
      { text: 'Pending Approvals', icon: <FactCheck />, path: '/admin/pending-approvals' },
      { text: 'Staff', icon: <Groups />, path: '/admin/teachers' },
      { text: 'Academics', icon: <School />, path: '/admin/classes' },
      { text: 'Attendance', icon: <FactCheck />, path: '/admin/attendance' },
      { text: 'Examinations', icon: <Assignment />, path: '/admin/exam-schedule' },
      { text: 'Timetable', icon: <CalendarMonth />, path: '/admin/academic-years' },
      { text: 'Fees & Payments', icon: <Payments />, path: '/admin/fee-payments' },
      { text: 'Transport', icon: <DirectionsBus />, path: '/admin/class-fees' },
      { text: 'Library', icon: <LibraryBooks />, path: '/admin/hall-tickets' },
      { text: 'Hostel', icon: <Hotel />, path: '/admin/class-assignments' },
      { text: 'Communications', icon: <Campaign />, path: '/admin/notifications' },
      { text: 'Reports', icon: <BarChart />, path: '/admin/reports' },
      { text: 'Settings', icon: <Settings />, path: '/admin/users' },
    ];
  };

  const isSelected = (path) => {
    if (path === '/dashboard') {
      return ['/dashboard', '/admin', '/teacher', '/student'].includes(location.pathname);
    }
    return location.pathname === path;
  };

  const todayLabel = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    weekday: 'short',
  });

  const portalTitle = user?.role === 'teacher'
    ? 'Teacher Portal'
    : user?.role === 'student'
      ? 'Student Portal'
      : 'Super Admin';

  const drawer = (
    <Box
      sx={{
        height: '100%',
        color: '#eaf2ff',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #071b44 0%, #08275a 48%, #061a3d 100%)',
      }}
    >
      <Box sx={{ px: 2.25, py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 48,
              height: 56,
              borderRadius: '12px 12px 18px 18px',
              border: `2px solid ${shell.gold}`,
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.06)',
            }}
          >
            <School sx={{ fontSize: 27 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              Basaveswhara
            </Typography>
            <Typography variant="body2" sx={{ color: '#d6e4ff', fontWeight: 700 }} noWrap>
              Lower Primary School
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: 1.5, flex: 1, overflowY: 'auto' }}>
        <List disablePadding>
          {getMenuItems().map((item) => {
            const selected = isSelected(item.path);
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.75 }}>
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }}
                  sx={{
                    minHeight: 44,
                    borderRadius: 1.5,
                    px: 1.4,
                    color: selected ? '#fff' : '#d4e1f7',
                    bgcolor: selected ? shell.active : 'transparent',
                    '&:hover': {
                      bgcolor: selected ? shell.active : 'rgba(255,255,255,0.08)',
                      color: '#fff',
                    },
                    '&.Mui-selected': {
                      bgcolor: shell.active,
                      boxShadow: '0 10px 24px rgba(29, 100, 216, 0.32)',
                      '&:hover': { bgcolor: shell.active },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 33,
                      color: 'inherit',
                      '& svg': { fontSize: 20 },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: selected ? 700 : 600 }}
                  />
                  {(user?.role === 'admin' || user?.role === 'superadmin') && item.text !== 'Dashboard' && (
                    <ExpandMore sx={{ fontSize: 18, opacity: 0.9 }} />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Security sx={{ color: '#fff', fontSize: 30 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" sx={{ color: '#c9d8ee', fontWeight: 600 }}>
                Academic Year
              </Typography>
              <Typography variant="body2" fontWeight={700} noWrap>
                2024-2025
              </Typography>
            </Box>
            <ExpandMore sx={{ fontSize: 18 }} />
          </Stack>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: shell.page }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'rgba(255,255,255,0.92)',
          color: shell.ink,
          borderBottom: `1px solid ${shell.line}`,
          backdropFilter: 'blur(16px)',
        }}
      >
        <Toolbar sx={{ minHeight: 72, gap: 2 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            aria-label="Open navigation"
          >
            <MenuIcon sx={{ fontSize: 30 }} />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          <Box
            sx={{
              width: { xs: '100%', sm: 330, lg: 430 },
              maxWidth: { xs: 260, sm: 'none' },
              height: 44,
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 1,
              px: 1.75,
              borderRadius: 2,
              border: `1px solid ${shell.line}`,
              bgcolor: '#fff',
            }}
          >
            <InputBase
              placeholder="Search students, staff, classes..."
              sx={{ flex: 1, fontSize: 14, color: shell.ink }}
            />
            <Search sx={{ color: '#52617a', fontSize: 22 }} />
          </Box>

          <IconButton sx={{ color: shell.ink, position: 'relative' }} aria-label="Notifications">
            <NotificationsNone />
            <Box
              sx={{
                position: 'absolute',
                right: 5,
                top: 8,
                minWidth: 18,
                height: 18,
                borderRadius: 9,
                bgcolor: '#2563eb',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              3
            </Box>
          </IconButton>

          <Box
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              gap: 1,
              px: 1.75,
              height: 44,
              borderRadius: 2,
              border: `1px solid ${shell.line}`,
              bgcolor: '#fff',
            }}
          >
            <CalendarMonth sx={{ color: '#52617a', fontSize: 20 }} />
            <Typography variant="body2" fontWeight={600} color={shell.ink}>
              {todayLabel}
            </Typography>
            <ExpandMore sx={{ color: '#52617a', fontSize: 18 }} />
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar sx={{ bgcolor: '#5b75ab', width: 42, height: 42, fontWeight: 700 }}>
              {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'A'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: shell.ink, fontWeight: 700 }} noWrap>
                {user?.full_name || user?.username || 'Admin'}
              </Typography>
              <Typography variant="caption" sx={{ color: shell.muted, fontWeight: 700 }} noWrap>
                {portalTitle}
              </Typography>
            </Box>
            <IconButton onClick={handleMenuOpen} sx={{ p: 0.5 }} aria-label="Open user menu">
              <ExpandMore />
            </IconButton>
          </Stack>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1,
                border: `1px solid ${shell.line}`,
                borderRadius: 2,
                boxShadow: '0 18px 36px rgba(15,31,63,0.12)',
              },
            }}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
              bgcolor: shell.navy,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: shell.page,
          pt: { xs: 10, md: 11 },
          px: { xs: 2, md: 3 },
          pb: 3,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
